import "./styles.css";
import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import PhoneInput from "@/UI/inputs/PhoneInput";
import { FormControl, InputLabel, MenuItem, OutlinedInput, Select, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";

export interface AdRequestPopupDetails {
    onSuccess: () => void;
}

interface Props {
    onClose: () => void;
    details?: AdRequestPopupDetails;
}

interface FormState {
    message: string;
    whatsapp: string | undefined;
    type: "product" | "store" | "menu" | "external" | "";
    intendedStartDate: string;
    intendedEndDate: string;
}

// ── MUI Shared Styles ─────────────────────────────────────────────────────────

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": { color: "var(--color-orange)" },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-lev3)",
        "& fieldset": { borderColor: "var(--color-gray-01)", borderWidth: "1.5px" },
        "& fieldset legend": { textAlign: dir === "rtl" ? "right" : "left" },
        "&:hover fieldset": { borderColor: "var(--color-gray-02)" },
        "&.Mui-focused fieldset": { borderColor: "var(--color-orange)", borderWidth: "1.5px" },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-select": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px !important",
        paddingRight: dir === "rtl" ? "14px !important" : "32px !important",
        paddingLeft: dir === "rtl" ? "32px !important" : "14px !important",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-icon": {
        right: dir === "rtl" ? "unset" : 7,
        left: dir === "rtl" ? 7 : "unset",
    },
});

const menuItemSx = { fontFamily: "Alexandria, sans-serif", fontSize: "13.5px" };

export default function AdRequestPopup({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const a = t.adBanners;
    const dir = lang === "ar" ? "rtl" : "ltr";

    const [form, setForm] = useState<FormState>({
        message: "",
        whatsapp: undefined,
        type: "",
        intendedStartDate: "",
        intendedEndDate: "",
    });
    const [error, setError] = useState("");

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (error) setError("");
    };

    const { loading, status, invoke } = useFetch("ad-requests", { method: "POST" }, false);

    const wasLoading = useRef(false);
    useEffect(() => {
        if (loading) {
            wasLoading.current = true;
        } else if (wasLoading.current) {
            wasLoading.current = false;
            if (status === 201) {
                details?.onSuccess();
                onClose();
            } else if (status === 409) {
                setError(lang === "ar" ? "لديك طلب نشط بالفعل" : "You already have an active request");
            } else if (status === 429) {
                setError(lang === "ar" ? "يمكنك تقديم طلب جديد بعد 7 أيام من الرفض" : "You can submit a new request 7 days after a rejection");
            } else if (status) {
                setError(lang === "ar" ? "حدث خطأ، يرجى المحاولة مرة أخرى" : "Something went wrong, please try again");
            }
        }
    }, [loading]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.message.trim()) return setError(a.messageRequired);
        if (!form.whatsapp) return setError(a.whatsappRequired);
        if (!form.type) return setError(a.typeRequired);
        invoke({
            data: {
                message: form.message.trim(),
                whatsapp: form.whatsapp.trim(),
                type: form.type || undefined,
                intendedStartDate: form.intendedStartDate || undefined,
                intendedEndDate: form.intendedEndDate || undefined,
            },
        });
    };

    const textFieldSx = getTextFieldSx(dir as "rtl" | "ltr");
    const today = new Date().toISOString().split("T")[0];

    return (
        <div className="arp-popup" dir={dir}>
            {/* Header */}
            <div className="arp-header">
                <h2 className="arp-title">{a.formTitle}</h2>
                <button type="button" className="arp-close" onClick={onClose}>
                    ×
                </button>
            </div>

            {/* Form */}
            <form className="arp-body" onSubmit={handleSubmit} noValidate>
                <div className="arp-fields">
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={6}
                        label={a.formMessage}
                        value={form.message}
                        onChange={(e) => setField("message", e.target.value)}
                        sx={textFieldSx}
                        slotProps={{ htmlInput: { style: { fontFamily: "Alexandria, sans-serif", fontSize: "13.5px" } } }}
                    />
                    <p className="arp-hint">{a.formMessageHint}</p>

                    <div className="arp-phone-wrap">
                        <span className="arp-phone-label">{a.formWhatsapp}</span>
                        <PhoneInput value={form.whatsapp} onChange={(v) => setField("whatsapp", v)} />
                    </div>

                    <FormControl fullWidth sx={textFieldSx}>
                        <InputLabel>{a.formType}</InputLabel>
                        <Select
                            value={form.type}
                            onChange={(e) => setField("type", e.target.value as FormState["type"])}
                            input={<OutlinedInput label={a.formType} />}
                            displayEmpty
                            renderValue={(v) =>
                                v ? (
                                    a.types[v as keyof typeof a.types]
                                ) : (
                                    <span style={{ color: "var(--color-gray-02)", fontFamily: "Alexandria, sans-serif" }}>{a.formTypeNone}</span>
                                )
                            }
                        >
                            <MenuItem value="product" sx={menuItemSx}>
                                {a.types.product}
                            </MenuItem>
                            <MenuItem value="store" sx={menuItemSx}>
                                {a.types.store}
                            </MenuItem>
                            <MenuItem value="menu" sx={menuItemSx}>
                                {a.types.menu}
                            </MenuItem>
                            <MenuItem value="external" sx={menuItemSx}>
                                {a.types.external}
                            </MenuItem>
                        </Select>
                    </FormControl>

                    <div className="arp-row">
                        <TextField
                            fullWidth
                            type="date"
                            label={a.formStartDate}
                            value={form.intendedStartDate}
                            onChange={(e) => setField("intendedStartDate", e.target.value)}
                            sx={textFieldSx}
                            slotProps={{ htmlInput: { min: today }, inputLabel: { shrink: true } }}
                        />
                        <TextField
                            fullWidth
                            type="date"
                            label={a.formEndDate}
                            value={form.intendedEndDate}
                            onChange={(e) => setField("intendedEndDate", e.target.value)}
                            sx={textFieldSx}
                            slotProps={{ htmlInput: { min: form.intendedStartDate || today }, inputLabel: { shrink: true } }}
                        />
                    </div>

                    {error && <p className="arp-error">{error}</p>}
                </div>

                {/* Footer */}
                <div className="arp-footer">
                    <button type="button" className="arp-btn arp-btn--cancel" onClick={onClose} disabled={loading}>
                        {a.formCancel}
                    </button>
                    <button type="submit" className="arp-btn arp-btn--submit" disabled={loading}>
                        {loading ? <span className="arp-spinner" /> : a.formSubmit}
                    </button>
                </div>
            </form>
        </div>
    );
}
